<template>
  <div>
    <spinner :is-loading="isLoadingContainer" />
    <div class="is-fluid">
      <div id="filter-content" class="container">
        <b-field grouped group-multiline position="is-centered">
          <b-field v-if="showInvestorFilter" custom-class="has-text-weight-normal">
            <b-select
              id="select-investor"
              v-model="filterInvestorId"
              size="is-small"
              name="investor"
              expanded
              rounded>
              <option :value="defaultInvestorSelected">Elige un inversionista</option>
              <option
                v-for="investor in investors"
                :key="investor._id"
                :value="investor._id">
                {{ investor.name }}
              </option>
            </b-select>
          </b-field>
          <b-field v-if="showInvestorFilter" custom-class="has-text-weight-normal">
            <b-button
              id="operation-filter-button"
              size="is-small"
              class="button perform-filter is-button-secondary is-rounded"
              :disabled="!checkUserFeature('element.my-portfolios.button.filtrar')"
              @click="getOperations">
              <span class="icon is-small">
                <i class="fas fa-filter" />
              </span>
              <span>Filtrar</span>
            </b-button>
          </b-field>
          <b-field v-if="operationListFromService.length > 0" id="operation-filter">
            <b-input
              id="search-input"
              v-model="searchInputVal"
              type="search"
              icon="search"
              icon-clickable
              placeholder="Número de Operación"
              size="is-small"
              rounded
              @input="filterOperation"
              @blur="sendFilterAction('filter')" />
          </b-field>
        </b-field>
      </div>
    </div>
    <spinner :is-loading="isLoading" />
    <div
      v-if="isErrorMessage"
      id="operation-error-title"
      class="operation-process-info has-text-centered error-message">
      <span>
        {{ errorMessage }}
      </span>
    </div>
    <div
      v-if="operationList.length"
      class="message-process-info has-text-centered transaction-details">
      <div id="message-process-title" class="message-text">
        <span v-if="operationList.length">
          La búsqueda arrojó un total <strong>{{ operationList.length }} operacion(es) </strong>.
        </span>
      </div>
    </div>
    <div
      v-if="operationList.length"
      class="message-process-info has-text-centered transaction-details">
      <div id="message-process-title" class="message-text">
        <span>
          Le pertenece(n) <strong>{{ operationListActualDueDate.length }} operacion(es) </strong> con cuota a vencer el <strong>{{ actualDueDate }}</strong>.
        </span>
      </div>
    </div>
    <div
      v-if="operationList.length"
      id="operation-list"
      name="operation-list"
      class="operation-details"
      appear
      tag="div">
      <div
        v-for="(operationItem, i) in operationList"
        :id="`item-${i}`"
        :key="`${operationItem}-${i}`"
        class="padding-top"
        :style="{ '--i': i }">
        <operation-item
          :operation="operationItem"
          :investors-ids="investorsIds"
          :pos="i" />
      </div>
    </div>
  </div>
</template>

<script>
import OperationItem from '@/ui/views/internal/my-portfolios/OperationItem.vue';
import { getInvestors } from '@/application/services/api-clients/investors';
import { getSummarizedOperationsByPaymentsInvestorIds } from '@/application/services/api-clients/operations';
import Roles from '@/domain/rolesEnum';
import userFeatures from '@/ui/mixins/user-features';
import Spinner from '../../../components/Spinner.vue';

export default {
  name: 'OperationList',
  components: { OperationItem, Spinner },
  props: {},
  data() {
    return {
      isLoading: false,
      isLoadingContainer: false,
      isErrorMessage: false,
      errorMessage: undefined,
      defaultInvestorSelected: '',
      investors: [],
      filterInvestorId: '',
      investorsIds: [],
      showInvestorFilter: false,
      operationList: [],
      operationListFromService: [],
      operationListActualDueDate: [],
      actualDueDate: '',
      searchInputVal: ''
    };
  },
  async mounted() {
    this.isLoadingContainer = true;
    const { user: { investorsIds, rol } } = this.$store.state.currentUser;
    const date = new Date();
    const month = date.getMonth() < 9 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
    this.actualDueDate = `${date.getFullYear()}-${month}-10`;
    if (this.isInstitucionalVendorOrAdmin(rol)) {
      this.investors = await getInvestors();
      if (this.investors.length > 0) {
        this.isLoadingContainer = false;
      }
      this.showInvestorFilter = true;
      this.isLoadingContainer = false;
    } else {
      this.investorsIds = investorsIds;
      await this.getOperationsByPaymentsInvestorIds(this.investorsIds, this.actualDueDate)
        .catch(() => {});
      this.sendFilterAction('load_module');
      this.isLoadingContainer = false;
    }
  },
  methods: {
    checkUserFeature(name) {
      return userFeatures.methods.checkUserFeature(name, this.$store);
    },
    async getOperations() {
      this.isLoading = true;
      this.operationList = [];
      this.operationListFromService = this.operationList;
      try {
        if (this.filterInvestorId) {
          this.investorsIds = [this.filterInvestorId];
          await this.getOperationsByPaymentsInvestorIds([this.filterInvestorId], this.actualDueDate);
          if (!this.operationList) {
            this.isErrorMessage = true;
            this.errorMessage = 'No hay operaciones disponibles para este inversionista.';
          } else {
            this.isErrorMessage = false;
          }
          this.isLoading = false;
        }
      } catch (e) {
        this.isErrorMessage = true;
        if (e.message === 'investorsIds not valid for the user') {
          this.errorMessage = 'No tienes permiso para ver las operaciones de este inversionista.';
        } else {
          this.errorMessage = 'Hubo un problema consultando las operaciones. Inténtalo de nuevo.';
        }
        this.isLoading = false;
      }
    },
    async sendFilterAction(actionFront) {
      try {
        const { currentUser } = this.$store.state;
        const { user } = currentUser;
        const action = {
          country: this.$i18n.locale,
          view: this.$route.name,
          user_id: user._id,
          user_rol: user.rol,
          executed_at: new Date(),
          action: actionFront
        };
        if (user.rol === 'INVESTOR' && user.investorsIds) {
          action.metadata = { investorsIds: user.investorsIds };
        }
        // await this.$socket.emit('action', action);
      } catch (_error) {
        // console.log('Error al enviar action filter', error);
      }
    },
    async getOperationsByPaymentsInvestorIds(investorsIds, actualDueDate) {
      this.operationList = await getSummarizedOperationsByPaymentsInvestorIds(investorsIds, actualDueDate);
      this.operationListFromService = this.operationList;
      this.operationListActualDueDate = this.operationList.filter(operation => operation.actualOperation);
    },
    isInstitucionalVendorOrAdmin(rol) {
      return rol === Roles.INSTITUCIONAL_VENDOR || rol === Roles.ADMIN; // || rol === undefined;
    },
    filterOperation() {
      this.operationList = this.operationListFromService;
      const filtered = this.operationList.filter(operation => operation.companyOperationId.toUpperCase().startsWith(this.searchInputVal.trim().toUpperCase()));
      if (!filtered || filtered.length === 0) {
        this.operationList = [];
        this.isErrorMessage = true;
        this.errorMessage = 'La operación no fue encontrada. Favor verifica.';
      } else {
        this.operationList = filtered;
        this.operationListActualDueDate = this.operationList.filter(operation => operation.actualOperation);
        this.isErrorMessage = false;
      }
    }
  }
};
</script>

<style scoped>
.padding-top {
  padding-top: 1rem;
}
.container {
  padding-bottom: 1rem;
}
.spinner-area {
  padding-top: 5rem;
}
.operation-process-info {
  display: flex;
  justify-content: center;
  color: red;
  grid-template-columns: 1fr 10fr;
  padding: 1rem;
  align-items: center;
  font-family: var(--secondary-font);
}
.message-process-info {
  grid-template-columns: 1fr 10fr;
  gap: 1rem;
  align-items: center;
  padding: 5px;
  margin: .5rem 0 .2rem 0;
  border-radius: 20px;
  font-family: var(--secondary-font);
  color: var(--color-nickel);
}
.message-process-info i {
  color: var(--primary);
}
</style>
